import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { Slide, Zoom } from "@mui/material";

import LoginPage from "./pages/Login/Login";
import Layout from "./Layout";
import NotFound from "./pages/NotFound/NotFound.jsx";
import { getIsAuthenticated, getAccountType } from "./store/auth/selectors";
import { checkPermission, PERMISSION_TYPES } from "./shared/utils/permissions";

const ProtectedRoute = ({ children, requiredPermission }) => {
  const loggedIn = useSelector(getIsAuthenticated);
  const accountType = useSelector(getAccountType);

  if (!loggedIn) {
    return <Navigate to="/login" />;
  }

  if (requiredPermission && !checkPermission(accountType, requiredPermission)) {
    return <Navigate to="/planner" />;
  }

  return children;
};

const SlidingLoginPage = () => {
  const location = useLocation();
  const cameFromLogout = location.state?.fromLogout === true;
  let slideDirection = "left";
  if (cameFromLogout) {
    slideDirection = "right";
  }

  return (
    <Slide
      direction={slideDirection}
      in={location.pathname === "/login"}
      timeout={800}
    >
      <div style={{ width: "100%", height: "100vh" }}>
        <LoginPage />
      </div>
    </Slide>
  );
};

const ZoomedLayout = ({ children }) => {
  const location = useLocation();

  return (
    <Zoom in={location.pathname !== "/login"} timeout={800}>
      <div style={{ width: "100%", height: "100vh" }}>{children}</div>
    </Zoom>
  );
};

const AppRouter = () => {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/login" element={<SlidingLoginPage />} />
        <Route path="/" element={<Navigate to="/planner" replace />} />
        <Route
          path="/planner"
          element={
            <ProtectedRoute requiredPermission={PERMISSION_TYPES.VIEW_EVENTS}>
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite-codes"
          element={
            <ProtectedRoute
              requiredPermission={PERMISSION_TYPES.VIEW_INVITE_CODES}
            >
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/students"
          element={
            <ProtectedRoute
              requiredPermission={PERMISSION_TYPES.VIEW_STUDENT_DETAILS}
            >
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/explore"
          element={
            <ProtectedRoute
              requiredPermission={PERMISSION_TYPES.VIEW_EXPLORE_STUDENT_DETAILS}
            >
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/schools"
          element={
            <ProtectedRoute requiredPermission={PERMISSION_TYPES.VIEW_SCHOOLS}>
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teams"
          element={
            <ProtectedRoute requiredPermission={PERMISSION_TYPES.VIEW_TEAMS}>
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bulk-upload-jobs"
          element={
            <ProtectedRoute
              requiredPermission={PERMISSION_TYPES.VIEW_BULK_UPLOAD_JOBS}
            >
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/global-events/:level"
          element={
            <ProtectedRoute
              requiredPermission={PERMISSION_TYPES.VIEW_GLOBAL_STUDENT_DETAILS}
            >
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/pdf-upload"
          element={
            <ProtectedRoute
              requiredPermission={PERMISSION_TYPES.UPLOAD_OMR_UPLOADS}
            >
              <ZoomedLayout>
                <Layout />
              </ZoomedLayout>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
};

export default AppRouter;
